import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { TreeNav } from 'ui-components/components/tree-navigation';

import { messages } from './messages';
import { SearchForm } from '../../components/shipping-grid/search-form';
import ToggleLeftNav from '../../components/toggle-left-nav';
import { useQuickSearchTerm } from '../../hooks/routing/use-quick-search-term';
import { BASE_FULFILLMENTS_PAGE_PATH, FULFILLMENTS_GRID_PATH } from '../../routes/constants';
import { QUICK_SEARCH_LOCATION_KEY } from '../../services/quicksearch-location-service';

/**
 * @param {Array} items
 * @param {string} location
 * @returns {Array}
 */
const updateNav = (items = [], location) => {
  return items.map((i) => ({
    ...i,
    items: updateNav(i.items, location),
    selected: i.location === location,
    expanded: i.items?.length && location.includes(i.location),
  }));
};

/**
 * @param {object} props
 * @param {Array} props.navItems
 * @returns {JSX.Element}
 */
export const FulfillmentsLeftNav = ({ navItems: defaultNavItems }) => {
  const location = useLocation();
  const [navItems, setNavItems] = useState(defaultNavItems);
  const quickSearchTerm = useQuickSearchTerm();
  const [searchInputValue, setSearchInputValue] = useState(quickSearchTerm ?? '');
  const isSearchActive = Boolean(quickSearchTerm);

  const isOnFulfillmentsGrid = useMemo(
    () => location.pathname.includes(`${BASE_FULFILLMENTS_PAGE_PATH}/${FULFILLMENTS_GRID_PATH}`),
    [location.pathname],
  );

  const handleClearSearchInput = useCallback(() => {
    setSearchInputValue('');
  }, []);

  const onItemSelected = useCallback((newSelectedNavItem) => {
    setNavItems((currentNavItems) => updateNav(currentNavItems, newSelectedNavItem.location));
  }, []);

  const buildTagProps = useCallback(
    (view) => {
      const isNavigatingToGrid = view.location.includes(FULFILLMENTS_GRID_PATH);
      const searchParams = new URLSearchParams();

      if (isNavigatingToGrid && quickSearchTerm) {
        searchParams.set(QUICK_SEARCH_LOCATION_KEY, quickSearchTerm);
      }

      return {
        to: {
          pathname: `${BASE_FULFILLMENTS_PAGE_PATH}/${view.location}`,
          search: searchParams.toString(),
        },
      };
    },
    [quickSearchTerm],
  );

  useEffect(() => {
    let view = '';

    if (location.pathname.startsWith(`${BASE_FULFILLMENTS_PAGE_PATH}/`)) {
      view = location.pathname.slice(BASE_FULFILLMENTS_PAGE_PATH.length + 1);
    }

    setNavItems(updateNav(defaultNavItems, view));
  }, [location.pathname, defaultNavItems]);

  useEffect(() => {
    if (isOnFulfillmentsGrid) {
      setSearchInputValue(quickSearchTerm ?? '');
    } else {
      setSearchInputValue('');
    }
  }, [isOnFulfillmentsGrid, quickSearchTerm]);

  return (
    <ToggleLeftNav>
      <SearchForm
        ariaLabelMessageDescriptor={messages.searchLabel}
        clearSearchInput={handleClearSearchInput}
        formTitleMessageDescriptor={messages.searchLabel}
        handleSearchInputChange={setSearchInputValue}
        isDisabled={!isOnFulfillmentsGrid}
        isHighlighted={isSearchActive}
        placeholderMessageDescriptor={messages.searchPlaceholder}
        searchInputValue={searchInputValue}
      />
      <div>
        <TreeNav buildTagProps={buildTagProps} isHighlighted={isSearchActive} items={navItems} onItemSelected={onItemSelected} />
      </div>
    </ToggleLeftNav>
  );
};
